import { acceptHMRUpdate, defineStore } from 'pinia'
import { toRaw } from 'vue'
import * as turf from '@turf/turf'
import { useMaps } from './maps'
import { useParkingLotsStore } from './parkingLots'
import { useDrawingStore } from './drawing'
import { useRoiStore } from './roi'
import {
    createParkingPathInRectangle,
    DEFAULT_PARKING_PATH_LINE_SPACING_METERS,
} from '../helpers/parkingLotPathGenerator'

const CENTER_PATH_STROKE_COLOR = '#F59E0B'
const SLOT_PATH_STROKE_COLOR = '#27B5FF'
const PATH_STROKE_WEIGHT = 3
const PATH_Z_INDEX = 1001

export const useNewPlannedParkingLotStore = defineStore('newPlannedParkingLot', {
    state: () => ({
        terraDrawFinishListener: null,
        terraDrawChangeListener: null,
        isDrawingRectangle: false,
        // While map measuring: do not sync/remove lots when TerraDraw features change
        _ignoreTerraDrawChanges: false,
    }),
    actions: {
        getLotBorderFeature(lot) {
            if (lot?.feature?.geometry?.type === 'Polygon') return lot.feature
            if (lot?.border?.type === 'Polygon') return { type: 'Feature', geometry: lot.border, properties: {} }
            return null
        },
        toTerraDrawStoreFeature(feature, id = null) {
            if (feature?.geometry?.type !== 'Polygon') return null
            const mapStore = useMaps()
            return {
                id: id ?? mapStore.terraDraw.getFeatureId(),
                type: 'Feature',
                geometry: JSON.parse(JSON.stringify(feature.geometry)),
                properties: { mode: 'angled-rectangle', field_geom_type: 'cutout' },
            }
        },
        toParkingLotFeature(terraDrawFeature) {
            const geometry = terraDrawFeature?.geometry
            if (geometry?.type !== 'Polygon') return null

            return {
                type: 'Feature',
                geometry: JSON.parse(JSON.stringify(geometry)),
                properties: { field_geom_type: 'cutout' },
            }
        },
        ensureTerraDrawActive() {
            const mapStore = useMaps()
            if (!mapStore.terraDraw) return false
            if (!mapStore.terraDraw.enabled) {
                mapStore.terraDraw.start()
            }
            return true
        },
        ensureChangeListener() {
            const mapStore = useMaps()
            if (!mapStore.terraDraw || this.terraDrawChangeListener) return

            this.terraDrawChangeListener = (ids) => {
                const pls = useParkingLotsStore()
                ids.forEach((featureId) => {
                    const lot = pls.newParkingLots.find(l => l.terraDrawFeatureId === featureId)
                        ?? pls.existingParkingLots.find(l => l.terraDrawFeatureId === featureId)
                    if (lot) this.syncLotBorderFromTerraDraw(lot)
                })
            }
            mapStore.terraDraw.on('change', this.terraDrawChangeListener)
        },
        cleanupChangeListener() {
            const mapStore = useMaps()
            if (this.terraDrawChangeListener && mapStore.terraDraw) {
                mapStore.terraDraw.off('change', this.terraDrawChangeListener)
                this.terraDrawChangeListener = null
            }
        },
        isExistingPlannedLot(lot) {
            const pls = useParkingLotsStore()
            return pls.isPlannedParkingSlot(lot) && !pls.getNewParkingLotById(lot?.id)
        },
        syncLotBorderFromTerraDraw(lot) {
            if (this._ignoreTerraDrawChanges) return
            if (!lot?.terraDrawFeatureId) return

            const mapStore = useMaps()
            const terraDraw = mapStore.terraDraw
            const featureId = lot.terraDrawFeatureId

            if (!terraDraw?.hasFeature(featureId)) {
                this.handleLotBorderRemovedFromTerraDraw(lot)
                return
            }

            const snapshot = terraDraw.getSnapshotFeature(featureId)
            const feature = this.toParkingLotFeature(snapshot)
            if (!feature) {
                this.handleLotBorderRemovedFromTerraDraw(lot)
                return
            }

            const pls = useParkingLotsStore()
            const isExisting = this.isExistingPlannedLot(lot)

            lot.feature = feature
            if (isExisting) {
                lot.border = JSON.parse(JSON.stringify(feature.geometry))
                lot.isModified = true
            }

            const editing = pls.getPlannedPathEditLot()
            if (pls.parkingLotForm && editing?.id === lot.id) {
                if (!isExisting || !pls.skipPlannedPathRegen) {
                    pls.applyPlannedPathFormToLot(lot)
                }
            }

            // Skip path regen for existing lots until user clicks Regenerate
            if (isExisting && pls.skipPlannedPathRegen) {
                this.displayStoredPathOnLot(lot)
            } else {
                this.applyPathToLot(lot)
            }
        },
        handleLotBorderRemovedFromTerraDraw(lot) {
            if (this._ignoreTerraDrawChanges) return
            if (!lot) return

            const isExisting = this.isExistingPlannedLot(lot)

            lot.terraDrawFeatureId = null
            lot.feature = null
            lot.path = null
            if (isExisting) {
                lot.border = null
                lot.isModified = true
            }
            this.removePathFromLot(lot)
        },
        clearLotPathPolylines(lot) {
            if (Array.isArray(lot?.pathPolylines)) {
                lot.pathPolylines.forEach(polyline => toRaw(polyline)?.setMap?.(null))
                lot.pathPolylines = []
            }
            if (lot?.id == null) return

            const pls = useParkingLotsStore()
            if (!pls.getNewParkingLotById(lot.id)) {
                pls.removeParkingLotMapOverlays({
                    pathIds: [`planned-path-${lot.id}`],
                })
            }
        },
        findPlannedLotById(lotId) {
            const pls = useParkingLotsStore()
            return pls.getNewParkingLotById(lotId)
                ?? pls.existingParkingLots.find(p => p.id === lotId && pls.isPlannedParkingSlot(p))
                ?? null
        },
        getActivePlannedEditLot() {
            const pls = useParkingLotsStore()
            if (pls.selectedNewParkingLotId != null) {
                return pls.getNewParkingLotById(pls.selectedNewParkingLotId)
            }
            if (pls.editingParkingLotId == null) return null
            const lot = pls.existingParkingLots.find(p => p.id === pls.editingParkingLotId)
            return pls.isPlannedParkingSlot(lot) ? lot : null
        },
        addLotToTerraDraw(lot) {
            if (!this.ensureTerraDrawActive()) return null

            const mapStore = useMaps()
            const borderFeature = this.getLotBorderFeature(lot)
            const terraFeature = this.toTerraDrawStoreFeature(borderFeature)
            if (!terraFeature) return null

            const results = mapStore.terraDraw.addFeatures([terraFeature])
            if (results.some(result => !result.valid)) {
                return null
            }

            lot.terraDrawFeatureId = terraFeature.id
            this.ensureChangeListener()
            return terraFeature.id
        },
        removeLotFromTerraDraw(lot) {
            const mapStore = useMaps()
            const id = lot?.terraDrawFeatureId
            if (!id || !mapStore.terraDraw?.hasFeature(id)) return

            mapStore.terraDraw.removeFeatures([id])
            lot.terraDrawFeatureId = null
        },
        removeAllLotsFromTerraDraw() {
            const pls = useParkingLotsStore()
            pls.newParkingLots.forEach(lot => this.removeLotFromTerraDraw(lot))
        },
        activateLotSelect(lotId) {
            const lot = this.findPlannedLotById(lotId)
            if (!lot?.terraDrawFeatureId) return

            if (!this.ensureTerraDrawActive()) return

            const mapStore = useMaps()
            this.ensureChangeListener()
            mapStore.terraDraw.setMode('select')
            mapStore.terraDraw.selectFeature(lot.terraDrawFeatureId)
        },
        startSelectEdit() {
            const lot = this.getActivePlannedEditLot()
            if (lot) this.activateLotSelect(lot.id)
        },
        beginNewLotEdit(lot) {
            if (this.isDrawingRectangle) {
                this.stopDrawingRectangle()
            }

            const mapStore = useMaps()
            if (lot.terraDrawFeatureId && !mapStore.terraDraw?.hasFeature(lot.terraDrawFeatureId)) {
                lot.terraDrawFeatureId = null
            }

            if (!lot?.feature?.geometry?.coordinates) {
                this.removePathFromLot(lot)
                return
            }

            if (!lot.terraDrawFeatureId) {
                this.addLotToTerraDraw(lot)
            }
            this.applyPathToLot(lot)
            this.activateLotSelect(lot.id)
        },
        beginExistingLotEdit(lot) {
            const pls = useParkingLotsStore()
            if (!pls.isPlannedParkingSlot(lot) || !pls.hasBorder(lot)) return

            if (!lot.feature?.geometry && lot.border) {
                lot.feature = {
                    type: 'Feature',
                    geometry: JSON.parse(JSON.stringify(lot.border)),
                    properties: { field_geom_type: 'cutout' },
                }
            }

            this.clearLotPathPolylines(lot)

            // Only remove overlays and add to TerraDraw when geometry edit enabled
            if (!pls.skipPlannedPathRegen) {
                pls.removeParkingLotMapOverlays({
                    parkingLotId: lot.id,
                    borderId: pls.getBorderId(lot),
                    pathIds: pls.getPathIds(lot),
                })

                if (!lot.terraDrawFeatureId) {
                    this.addLotToTerraDraw(lot)
                }
                if (lot.terraDrawFeatureId) {
                    this.activateLotSelect(lot.id)
                }
            }

            this.displayStoredPathOnLot(lot)
        },
        finalizePlannedLotEdit(lot, { syncFromTerra = false } = {}) {
            if (!lot) return

            const pls = useParkingLotsStore()
            if (syncFromTerra && lot.terraDrawFeatureId) {
                this.syncLotBorderFromTerraDraw(lot)
                this.removeLotFromTerraDraw(lot)
            } else {
                this.removePathFromLot(lot)
                if (lot.terraDrawFeatureId) {
                    this.removeLotFromTerraDraw(lot)
                }
            }

            pls.removeParkingLotMapOverlays({
                parkingLotId: lot.id,
                borderId: pls.getBorderId(lot),
                pathIds: pls.getPathIds(lot),
            })
            pls.drawParkingLotOnMap(lot)
            pls.refreshAllParkingSignIcons()
        },
        endExistingLotEdit(lot) {
            if (lot?.terraDrawFeatureId) this.finalizePlannedLotEdit(lot, { syncFromTerra: true })
        },
        endAllExistingPlannedTerraEdits(keepLotId = null) {
            const pls = useParkingLotsStore()
            pls.existingParkingLots
                .filter(lot => pls.isPlannedParkingSlot(lot) && lot.terraDrawFeatureId && lot.id !== keepLotId)
                .forEach(lot => this.finalizePlannedLotEdit(lot, { syncFromTerra: false }))
        },
        buildPathForLot(lot) {
            const feature = this.getLotBorderFeature(lot)
            if (!feature) return null

            try {
                const pls = useParkingLotsStore()
                return createParkingPathInRectangle(
                    feature,
                    lot.pathLineSpacingMeters ?? DEFAULT_PARKING_PATH_LINE_SPACING_METERS,
                    pls.getCenterLineOptionsForLot(lot),
                ).geometry
            } catch (error) {
                console.error('Failed to build parking path:', error)
                return null
            }
        },
        removePathFromLot(lot) {
            this.clearLotPathPolylines(lot)

            const pls = useParkingLotsStore()
            if (lot?.id != null && !pls.getNewParkingLotById(lot.id)) {
                const pathIds = pls.getPathIds(lot)
                if (pathIds.length > 0) {
                    pls.removeParkingLotMapOverlays({ pathIds })
                }
            }
        },
        drawPathPolylines(lot) {
            if (lot?.path?.type !== 'MultiLineString' || !lot.path.coordinates?.length) {
                return
            }

            const mapStore = useMaps()
            if (!mapStore.map || !mapStore.google) return

            this.clearLotPathPolylines(lot)

            const pls = useParkingLotsStore()
            const pathId = `planned-path-${lot.id}`
            const map = toRaw(mapStore.map)

            lot.pathPolylines = []
            lot.path.coordinates.forEach((ring, index) => {
                const strokeColor = index === 0 ? CENTER_PATH_STROKE_COLOR : SLOT_PATH_STROKE_COLOR
                const polyline = new mapStore.google.maps.Polyline({
                    path: ring.map(([lng, lat]) => ({ lat, lng })),
                    strokeColor,
                    strokeOpacity: 1,
                    strokeWeight: PATH_STROKE_WEIGHT,
                    zIndex: PATH_Z_INDEX,
                })
                polyline.setMap(map)
                const raw = toRaw(polyline)
                raw.parkingLotId = lot.id
                raw.pathId = pathId
                pls.attachParkingLotMapStyles(raw, 'polyline', { strokeColor })
                lot.pathPolylines.push(raw)
                if (!pls.getNewParkingLotById(lot.id)) {
                    pls.parkingLots.push(raw)
                }
            })

            if (pls.selectedParkingLotId === lot.id || pls.editingParkingLotId === lot.id) {
                pls.selectParkingLot(lot.id)
            }
        },
        displayStoredPathOnLot(lot) {
            if (!lot) return
            this.removePathFromLot(lot)
            this.drawPathPolylines(lot)
        },
        applyPathToLot(lot) {
            if (!lot) return

            this.removePathFromLot(lot)

            lot.path = this.buildPathForLot(lot)
            this.drawPathPolylines(lot)
        },
        toggleDrawingRectangle() {
            if (this.isDrawingRectangle) {
                this.stopDrawingRectangle()
            } else {
                this.startDrawingRectangle()
            }
        },
        getLinkedTerraDrawFeatureIds() {
            const pls = useParkingLotsStore()
            const ids = new Set()
            pls.newParkingLots.forEach(lot => {
                if (lot.terraDrawFeatureId) ids.add(lot.terraDrawFeatureId)
            })
            pls.existingParkingLots.forEach(lot => {
                if (pls.isPlannedParkingSlot(lot) && lot.terraDrawFeatureId) {
                    ids.add(lot.terraDrawFeatureId)
                }
            })
            return ids
        },
        clearOrphanRectangleFeatures() {
            const mapStore = useMaps()
            if (!mapStore.terraDraw) return

            const linkedIds = this.getLinkedTerraDrawFeatureIds()
            const orphanIds = (mapStore.terraDraw.getSnapshot() ?? [])
                .filter(f => f.properties?.mode === 'angled-rectangle' && !linkedIds.has(f.id))
                .map(f => f.id)

            if (orphanIds.length) {
                mapStore.terraDraw.removeFeatures(orphanIds)
            }
        },
        startDrawingRectangle() {
            const mapStore = useMaps()

            if (!mapStore.terraDraw) {
                console.error('TerraDraw not initialized')
                return
            }

            this.cleanupOtherDrawingListeners()
            this.cleanupDrawingListener()
            this.ensureTerraDrawActive()
            this.ensureChangeListener()

            mapStore.terraDraw.setMode('angled-rectangle')
            this.isDrawingRectangle = true

            this.terraDrawFinishListener = (id) => {
                const features = mapStore.terraDraw.getSnapshot()
                const feature = features.find(f => f.id === id)
                if (feature) {
                    this.handleDrawingComplete(feature)
                }
            }
            mapStore.terraDraw.on('finish', this.terraDrawFinishListener)
        },
        stopDrawingRectangle() {
            this.clearOrphanRectangleFeatures()
            this.cancelDrawing()
        },
        handleDrawingComplete(terraDrawFeature) {
            const mapStore = useMaps()
            const pls = useParkingLotsStore()

            const feature = this.toParkingLotFeature(terraDrawFeature)
            if (!feature) {
                mapStore.terraDraw.removeFeatures([terraDrawFeature.id])
                this.finishDrawingSession()
                return
            }

            const newLot = pls.addNewParkingLotFromFeature(feature, terraDrawFeature.id)
            if (newLot) {
                pls.selectNewParkingLot(newLot.id)
                pls.centerMapOnParkingLot(newLot)
            }

            this.finishDrawingSession()
        },
        finishDrawingSession() {
            this.cleanupDrawingListener()
            const mapStore = useMaps()
            if (mapStore.terraDraw?.enabled) {
                mapStore.terraDraw.setMode('select')
            }
            this.isDrawingRectangle = false
        },
        cleanupOtherDrawingListeners() {
            const mapStore = useMaps()
            const ds = useDrawingStore()
            const roiStore = useRoiStore()

            ds.cleanupDrawingListener()
            if (roiStore.terraDrawListener) {
                mapStore.terraDraw.off('finish', roiStore.terraDrawListener)
                roiStore.terraDrawListener = null
            }
        },
        cleanupDrawingListener() {
            const mapStore = useMaps()
            if (this.terraDrawFinishListener && mapStore.terraDraw) {
                mapStore.terraDraw.off('finish', this.terraDrawFinishListener)
                this.terraDrawFinishListener = null
            }
        },
        cancelDrawing() {
            this.cleanupDrawingListener()
            const mapStore = useMaps()
            if (mapStore.terraDraw?.enabled) {
                mapStore.terraDraw.setMode('select')
            }
            this.isDrawingRectangle = false
        },
        exitParkingLotCreationMode() {
            this.isDrawingRectangle = false
            this.cleanupDrawingListener()
            this.cleanupChangeListener()
            this.endAllExistingPlannedTerraEdits()
            this.removeAllLotsFromTerraDraw()

            const mapStore = useMaps()
            if (mapStore.terraDraw?.enabled) {
                mapStore.terraDraw.setMode('select')
                mapStore.terraDraw.stop()
            }
        },
    },
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useNewPlannedParkingLotStore, import.meta.hot))
}
